import { useTranslation } from '@thoughtspot/i18n';
import { ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { WizardModal, } from '@thoughtspot/radiant-react/widgets/wizard-modal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PRIMARY_ORG_ID, TranslationsKeys, } from '../variable-interface';
import { trackVariableCreateAbandoned } from '../variables-analytics';
import { AssignValuesStep, VariableDetailsStep, } from './create-edit-form-wizard';
import styles from './create-edit-modal.module.scss';
import { useOrgMappings } from './hooks/useOrgMappings';
import { useVariableForm } from './hooks/useVariableForm';
export const CreateEditForm = ({ isModalOpened, closeModal, isEditMode, variableData, orgList, onSuccess, externalNameError, clearExternalNameError, isOrgsLoading = false, checkVariableNameExists, isCheckingVariableName, }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { name, variableType, sensitive, nameError, setNameError, handleNameChange, handlePurposeChange, handleSensitiveChange, validateForm, } = useVariableForm({
        isEditMode,
        variableData,
        externalNameError,
        clearExternalNameError,
    });
    const { orgMappings, rowErrors, handleAddRow, handleRemoveRow, handleMappingNameChange, handleValueChange, validateOrgMappingRows, nameToIdMap, resolveOrgIdentifier, originalOrgIdentifiersRef, setRowErrors, hasChanges, hasValidContent, } = useOrgMappings({
        isEditMode,
        variableData,
        orgList,
        isModalOpened,
        sensitive,
    });
    useEffect(() => {
        if (isModalOpened) {
            setCurrentStep(0);
            setNameError(null);
            setRowErrors({});
        }
    }, [isModalOpened, setNameError, setRowErrors]);
    useEffect(() => {
        if (externalNameError && !isEditMode) {
            setNameError(externalNameError);
            if (currentStep !== 0) {
                setCurrentStep(0);
            }
        }
    }, [externalNameError, isEditMode, currentStep, setNameError]);
    const orgOptions = useMemo(() => orgList?.map(org => ({
        id: String(org.orgId),
        label: org.orgName,
    })) || [], [orgList]);
    const orgOptionsGroups = useMemo(() => [{ options: orgOptions }], [
        orgOptions,
    ]);
    const createdOrgId = variableData?.owner_org_id;
    const getFilteredOrgOptions = useCallback((currentRowId, currentRowOrgId) => {
        const selectedOrgIds = new Set();
        orgMappings.forEach(mapping => {
            if (mapping.id !== currentRowId && mapping.orgId) {
                selectedOrgIds.add(mapping.orgId);
            }
        });
        let filteredOptions = orgOptions.filter(option => option.id === currentRowOrgId ||
            !selectedOrgIds.has(option.id));
        if (createdOrgId && createdOrgId !== PRIMARY_ORG_ID) {
            filteredOptions = filteredOptions.filter(option => option.id === createdOrgId);
        }
        return [{ options: filteredOptions }];
    }, [orgMappings, orgOptions, createdOrgId]);
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }
        if (!validateOrgMappingRows()) {
            return;
        }
        setIsSubmitting(true);
        setNameError(null);
        setRowErrors({});
        const changedOrNewMappings = [];
        const unchangedSensitiveIdentifiers = new Set();
        orgMappings.forEach(mapping => {
            if (!mapping.orgId)
                return;
            if (sensitive && mapping.isExisting && !mapping.value) {
                if (mapping.orgIdentifier) {
                    unchangedSensitiveIdentifiers.add(mapping.orgIdentifier);
                }
                return;
            }
            if (mapping.value) {
                changedOrNewMappings.push(mapping);
            }
        });
        const validOrgMappings = changedOrNewMappings.map(mapping => {
            const orgIdentifier = mapping.orgIdentifier || resolveOrgIdentifier(mapping.orgId);
            return {
                orgId: mapping.orgId,
                orgIdentifier,
                value: mapping.value,
                isExisting: mapping.isExisting || false,
            };
        });
        const existingOrgIdentifierSet = new Set(validOrgMappings
            .filter(mapping => mapping.isExisting && mapping.orgIdentifier)
            .map(mapping => mapping.orgIdentifier));
        unchangedSensitiveIdentifiers.forEach(identifier => {
            existingOrgIdentifierSet.add(identifier);
        });
        const removedIdentifiers = isEditMode && originalOrgIdentifiersRef.current.size > 0
            ? Array.from(originalOrgIdentifiersRef.current).filter(identifier => identifier &&
                !existingOrgIdentifierSet.has(identifier))
            : [];
        const formData = {
            name,
            variableType,
            sensitive,
            orgMappings: validOrgMappings,
            removedMappings: removedIdentifiers.map(identifier => ({
                orgId: nameToIdMap.get(identifier) || '',
                orgIdentifier: identifier,
            })),
        };
        try {
            await onSuccess(formData);
        }
        catch (error) {
            setIsSubmitting(false);
        }
    };
    const handleStep1Next = async () => {
        if (!validateForm()) {
            return;
        }
        if (!isEditMode && checkVariableNameExists) {
            const nameExists = await checkVariableNameExists(name);
            if (nameExists) {
                return;
            }
        }
        setNameError(null);
        setCurrentStep(1);
    };
    const handleBackToStep1 = () => {
        setRowErrors({});
        setCurrentStep(0);
    };
    const handleCancel = useCallback(() => {
        if (!isEditMode && !isSubmitting) {
            trackVariableCreateAbandoned({
                step: currentStep,
                has_name: name.trim().length > 0,
                has_values: orgMappings.some(m => m.value),
                variable_type: variableType,
            });
        }
        closeModal();
    }, [
        isEditMode,
        isSubmitting,
        currentStep,
        name,
        orgMappings,
        variableType,
        closeModal,
    ]);
    const wizardSteps = isEditMode
        ? [
            {
                stepTitle: t(TranslationsKeys.ASSIGN_VALUES),
                stepContent: (React.createElement(AssignValuesStep, { orgMappings: orgMappings, orgOptionsGroups: orgOptionsGroups, variableType: variableType, rowErrors: rowErrors, isOrgsLoading: isOrgsLoading, sensitive: sensitive, createdOrgId: variableData?.owner_org_id, onAddRow: handleAddRow, onRemoveRow: handleRemoveRow, onMappingNameChange: handleMappingNameChange, onValueChange: handleValueChange, getFilteredOrgOptions: getFilteredOrgOptions })),
                stepForwardButton: {
                    text: t(TranslationsKeys.SAVE),
                    onNext: handleSubmit,
                    isDisabled: isSubmitting || !hasChanges,
                    isLoading: isSubmitting,
                },
            },
        ]
        : [
            {
                stepTitle: t(TranslationsKeys.ADD_DETAILS),
                stepContent: (React.createElement(VariableDetailsStep, { name: name, variableType: variableType, sensitive: sensitive, nameError: nameError, isEditMode: isEditMode, onNameChange: handleNameChange, onPurposeChange: handlePurposeChange, onSensitiveChange: handleSensitiveChange })),
                stepForwardButton: {
                    text: t(TranslationsKeys.NEXT),
                    onNext: handleStep1Next,
                    isDisabled: isSubmitting,
                    isLoading: isCheckingVariableName,
                },
            },
            {
                stepTitle: t(TranslationsKeys.ASSIGN_VALUES),
                stepContent: (React.createElement(AssignValuesStep, { orgMappings: orgMappings, orgOptionsGroups: orgOptionsGroups, variableType: variableType, rowErrors: rowErrors, isOrgsLoading: isOrgsLoading, sensitive: sensitive, createdOrgId: variableData?.owner_org_id, onAddRow: handleAddRow, onRemoveRow: handleRemoveRow, onMappingNameChange: handleMappingNameChange, onValueChange: handleValueChange, getFilteredOrgOptions: getFilteredOrgOptions })),
                stepForwardButton: {
                    text: t(TranslationsKeys.SAVE),
                    onNext: handleSubmit,
                    isDisabled: isSubmitting || !hasValidContent,
                    isLoading: isSubmitting,
                },
                stepBackButton: {
                    text: t(TranslationsKeys.BACK),
                    onBack: handleBackToStep1,
                },
            },
        ];
    return (React.createElement(WizardModal, { isOpen: isModalOpened, size: ModalSize.MEDIUM, title: t(isEditMode
            ? TranslationsKeys.MODAL_EDIT_TITLE
            : TranslationsKeys.MODAL_CREATE_TITLE), onDismiss: handleCancel, denyButton: {
            text: t(TranslationsKeys.CANCEL),
            onDeny: handleCancel,
        }, wizardModalStep: wizardSteps, currentStep: currentStep, isProgressBarEnabled: !isEditMode, includesHeader: true, includesFooter: true, className: styles.wizardModal, isContentLoading: isOrgsLoading }));
};
//# sourceMappingURL=create-edit-form.container.js.map